// src/components/NotificationSystem.jsx
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import {
  FiCheckCircle, FiAlertCircle, FiTrash2, FiRefreshCw, FiInfo,
  FiX, FiAlertTriangle, FiArrowLeft, FiCheck, FiEdit2,
  FiEye, FiVideo, FiGrid, FiCamera
} from 'react-icons/fi';
import { FaRuler, FaMapPin } from 'react-icons/fa';
import { TbWallpaper, TbAtom2 } from 'react-icons/tb';
import { CiExport } from 'react-icons/ci';
import { RiImageCircleAiLine, RiVideoOnAiLine } from 'react-icons/ri';
import { LuConstruction } from 'react-icons/lu';
import { getUserPlanCaps, PLAN_CAPS } from '../services/usageService';

// ============================================
// CONSTANTS (aligned with internal economics)
// ============================================
const ITERATION_DOLLAR_COST = 0.065;   // $0.065 per iteration (Segmind blended cost)
const VIDEO_DOLLAR_COST = 1.79;        // $1.79 per video (Kling O3 average)

// Monthly plan prices (public‑facing)
const PLAN_PRICES = {
  starter: 0,
  creator: 45,
  pro: 100,
};

// Top‑up service fee: 10% added to the generation budget, shown transparently
const SERVICE_FEE_RATE = 0.10;

// ============================================
// COMING SOON SECTIONS (hero modals)
// ============================================
export const COMING_SOON_SECTIONS = {
  ARCHITECTURAL_TOOLS: {
    title: 'Architectural Tools',
    description: 'A powerful suite of tools for architectural planning, annotation, and collaboration. We are rolling out these features in phases.',
    image: null,
    launchDate: 'Coming 2026',
    items: [
      {
        icon: <FaRuler size={24} />,
        label: 'Measurement',
        description: 'Annotate dimensions directly on your design for precise planning.',
      },
      {
        icon: <FiEdit2 size={24} />,
        label: 'Floor Plan Sketch',
        description: 'Draw basic layouts alongside your photo edit to overlay floor plans.',
      },
      {
        icon: <FaMapPin size={24} />,
        label: 'Structural Notes',
        description: 'Flag load‑bearing walls, electrical outlets, or other key structural elements.',
      },
      {
        icon: <CiExport size={24} />,
        label: 'Export with Annotations',
        description: 'Share planning context with clients or contractors including all notes and sketches.',
      }
    ]
  },
  CREATIVE_CATALOG: {
    title: 'Creative Catalog',
    description: 'Explore our curated collection of design assets, materials, and textures.',
    image: null,
    items: []
  },
  WALL_COLOR_TOOL: {
    title: 'Wall Color Tool',
    description: 'Transform any room with a single click. Select walls in your photo and instantly change colors to see different palettes. Coming in Q1 2026.',
    image: null,
    launchDate: 'Coming Q1 2026',
    items: [
      {
        icon: <TbWallpaper size={24} />,
        label: 'Smart Wall Detection',
        description: 'AI automatically detects walls, doors, and windows for precise color application.',
      },
      {
        icon: <FiEdit2 size={24} />,
        label: 'Color Palette Library',
        description: 'Browse curated color collections from top paint brands or create your custom palette.',
      },
      {
        icon: <FiCheck size={24} />,
        label: 'Real-time Preview',
        description: 'See color changes instantly with realistic lighting and shadow simulation.',
      },
      {
        icon: <FiEye size={24} />,
        label: 'Before & After Compare',
        description: 'Swipe between original and new colors to see the transformation.',
      }
    ]
  },
  DYNAMIC_CANVAS: {
    title: 'Dynamic Canvas',
    description: 'Bring your designs to life with interactive 3D parallax and depth effects. Coming in Q2 2026.',
    image: null,
    launchDate: 'Coming Q2 2026',
    items: [
      {
        icon: <TbAtom2 size={24} />,
        label: '3D Parallax',
        description: 'Tilt your device or use mouse to view your design from different angles with depth.',
      },
      {
        icon: <FiVideo size={24} />,
        label: 'Animated Walkthrough',
        description: 'Create smooth cinematic walkthroughs of your designed spaces.',
      },
      {
        icon: <FiGrid size={24} />,
        label: 'Depth Maps',
        description: 'AI-generated depth maps create realistic 3D effects from any 2D image.',
      },
      {
        icon: <FiCamera size={24} />,
        label: 'Interactive Viewing',
        description: 'Share interactive 3D designs with clients for immersive presentations.',
      }
    ]
  },
};

// ============================================
// NOTIFICATION TRACKING LABELS
// ============================================

/** Possible triggers for the full‑screen modal */
export const FULLSCREEN_TRIGGERS = {
  TRIAL_ENDED: 'trial-ended',
  NOT_ENOUGH_BALANCE_ITERATIONS: 'not-enough-balance-iterations',
  NOT_ENOUGH_BALANCE_VIDEOS: 'not-enough-balance-videos',
  ACCOUNT_SUSPENDED: 'account-suspended',
  TOS_VIOLATION: 'tos-violation',
};

/** Pre‑built toast notification types for easy tracking */
export const TOAST_IDS = {
  PAYMENT_DECLINED: 'payment-declined',
  LOW_BALANCE_WARNING: 'low-balance-warning',
  GENERATION_FAILED: 'generation-failed',
  SESSION_TIMEOUT: 'session-timeout',
  SUBSCRIPTION_RENEWED: 'subscription-renewed',
  PROJECT_SAVED: 'project-saved',
  AUTO_SAVE: 'auto-save',
  RENDER_CREATED: 'render-created',
  RENDER_SAVED: 'render-saved',
  ASSET_UPLOADED: 'asset-uploaded',
  PROJECT_DELETED: 'project-deleted',
  ASSET_DELETED: 'asset-deleted',
  RENDER_DELETED: 'render-deleted',
  RENDER_LIKED: 'render-liked',
  DISPLAY_NAME_SAVED: 'display-name-saved',
  AVATAR_SAVED: 'avatar-saved',
  EMAIL_CHANGED: 'email-changed',
  PASSWORD_CHANGED: 'password-changed',
  EXPORT_COMPLETED: 'export-completed',
};

/** Labels for push notifications (PWA) – used when scheduling or handling push events */
export const PUSH_NOTIFICATION_IDS = {
  LOW_BALANCE_REMINDER: 'low-balance-reminder',
  MAINTENANCE_ALERT: 'maintenance-alert',
  SUBSCRIPTION_RENEWAL_COMING_SOON: 'subscription-renewal-coming-soon',
  NEW_LOGIN_LOCATION: 'new-login-location',
};

// ============================================
// 1. CONTEXT
// ============================================
const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within NotificationProvider');
  return context;
};

/**
 * Hook that returns labelled toast helper functions.
 * Usage: const toasts = useToastNotifications(); toasts.projectSaved();
 * All usages can be found by searching for `toasts.` + the method name.
 */
export const useToastNotifications = () => {
  const { showToast } = useNotification();
  return useMemo(() => ({
    paymentDeclined: () => showToast('Payment declined', 'error', 5000),
    lowBalanceWarning: () =>
      showToast(
        'Your generation budget is running low. Please add additional balance to continue creating.',
        'warning',
        5000
      ),
    generationFailed: () => showToast('Generation failed', 'error', 5000),
    sessionTimeout: () => showToast('Session timeout', 'warning', 5000),
    subscriptionRenewed: () => showToast('Subscription renewed successfully', 'success', 4000),
    projectSaved: () => showToast('Project saved', 'success', 3000),
    autoSave: () => showToast('Auto save', 'info', 3000),
    renderCreated: () => showToast('Render created', 'success', 3000),
    renderSaved: () => showToast('Render saved', 'success', 3000),
    assetUploaded: () => showToast('Asset uploaded', 'success', 3000),
    projectDeleted: () => showToast('Project deleted', 'success', 3000),
    assetDeleted: () => showToast('Asset deleted', 'success', 3000),
    renderDeleted: () => showToast('Render deleted', 'success', 3000),
    renderLiked: () => showToast('Render liked', 'success', 3000),
    displayNameSaved: () => showToast('Display name saved', 'success', 3000),
    avatarSaved: () => showToast('Avatar saved', 'success', 3000),
    emailChanged: () => showToast('Email changed', 'success', 3000),
    passwordChanged: () => showToast('Password changed', 'success', 3000),
    exportCompleted: () => showToast('Export completed', 'success', 4000),
  }), [showToast]);
};

// ============================================
// 2. PROVIDER
// ============================================
export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const [modalConfig, setModalConfig] = useState(null);
  const [modalKey, setModalKey] = useState(0);  // forces remount on each new modal

  // New: generic modal state (tutorial or coming soon)
  const [genericModal, setGenericModal] = useState(null); // { type: 'tutorial' | 'comingSoon', props: {...} }

  // ----- Toast -----
  const showToast = useCallback((message, type = 'success', duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, duration }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // ----- Confirmation Modal (legacy) -----
  const showConfirm = useCallback((message, onConfirm) => {
    setConfirmState({ message, onConfirm });
  }, []);

  const hideConfirm = useCallback(() => setConfirmState(null), []);

  // ----- Full‑Screen Modal (new) -----
  const showFullScreenModal = useCallback((config) => {
    setModalKey(prev => prev + 1);
    setModalConfig(config);
  }, []);

  const hideFullScreenModal = useCallback(() => setModalConfig(null), []);

  // ----- Tutorial Modal -----
  const showTutorialModal = useCallback((voiceEnabled, onToggleVoice) => {
    setGenericModal({
      type: 'tutorial',
      props: { voiceEnabled, onToggleVoice }
    });
  }, []);

  // ----- Coming Soon Modal -----
  const showComingSoonModal = useCallback((title, description, image, launchDate) => {
    setGenericModal({
      type: 'comingSoon',
      props: { title, description, image, launchDate }
    });
  }, []);

  // ----- Coming Soon Section (by key) -----
  const showComingSoonSection = useCallback((sectionKey) => {
    const section = COMING_SOON_SECTIONS[sectionKey];
    if (!section) {
      console.warn(`Unknown coming soon section: ${sectionKey}`);
      return;
    }
    // If the section has items, show the list modal, otherwise the simple one
    if (section.items && section.items.length > 0) {
      setGenericModal({
        type: 'comingSoonList',
        props: { title: section.title, description: section.description, items: section.items, image: section.image, launchDate: section.launchDate }
      });
    } else {
      showComingSoonModal(section.title, section.description, section.image, section.launchDate);
    }
  }, [showComingSoonModal]);

  const hideGenericModal = useCallback(() => setGenericModal(null), []);

  return (
    <NotificationContext.Provider value={{ showToast, showConfirm, showFullScreenModal, showTutorialModal, showComingSoonModal, showComingSoonSection }}>
      {children}

      {/* Toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Confirmation Modal */}
      {confirmState && (
        <ConfirmModal
          message={confirmState.message}
          onConfirm={() => {
            confirmState.onConfirm();
            hideConfirm();
          }}
          onClose={hideConfirm}
        />
      )}

      {/* Full‑Screen Modal (key forces fresh mount = fresh loader) */}
      {modalConfig && (
        <FullScreenModal key={modalKey} config={modalConfig} onClose={hideFullScreenModal} />
      )}

      {/* Generic Modal (Tutorial or Coming Soon) */}
      {genericModal && (
        genericModal.type === 'tutorial' ? (
          <TutorialModal
            {...genericModal.props}
            onClose={hideGenericModal}
          />
        ) : genericModal.type === 'comingSoon' ? (
          <ComingSoonModal
            {...genericModal.props}
            onClose={hideGenericModal}
          />
        ) : genericModal.type === 'comingSoonList' ? (
          <ComingSoonListModal
            {...genericModal.props}
            onClose={hideGenericModal}
          />
        ) : null
      )}
    </NotificationContext.Provider>
  );
};

// ============================================
// 3. TOAST COMPONENT
// ============================================
const Toast = ({ message, type = 'success', duration, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [onClose, duration]);

  const colorMap = {
    success: { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32', icon: '#4caf50' },
    error:   { bg: '#ffebee', border: '#f44336', text: '#c62828', icon: '#f44336' },
    warning: { bg: '#fff3e0', border: '#ff9800', text: '#e65100', icon: '#ff9800' },
    info:    { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0', icon: '#2196f3' },
    'auto-delete': { bg: '#e8f0ff', border: '#88b7ff', text: '#4a6fa5', icon: '#88b7ff' },
  };
  const colors = colorMap[type] || colorMap.success;

  const IconMap = {
    success: FiCheckCircle,
    error:   FiAlertCircle,
    warning: FiTrash2,
    info:    FiInfo,
    'auto-delete': FiRefreshCw,
  };
  const Icon = IconMap[type] || FiCheckCircle;

  return (
    <div style={{
      position: 'fixed',
      top: '80px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 10000,
      animation: 'toastSlideDown 0.3s ease-out, toastFadeOut 0.3s ease-in 2.7s forwards',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: colors.bg,
        borderLeft: `4px solid ${colors.border}`,
        borderRadius: '12px',
        padding: '12px 20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        fontFamily: 'Avenir, sans-serif',
        minWidth: '280px',
        width: 'max-content',
        maxWidth: 'min(90vw, 480px)',
        border: `1px solid ${colors.border}`,
      }}>
        <Icon size={20} color={colors.icon} style={{ flexShrink: 0 }} />
        <div style={{ fontSize: '0.85rem', fontWeight: 500, color: colors.text, textAlign: 'center' }}>
          {message}
        </div>
        <button onClick={onClose} style={{
          background: 'transparent',
          border: 'none',
          borderRadius: '50%',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: colors.text,
          fontSize: '14px',
          opacity: 0.6,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '1'}
        onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}>
          ✕
        </button>
      </div>
      <style>{`
        @keyframes toastSlideDown {
          from { transform: translateX(-50%) translateY(-20px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
        @keyframes toastFadeOut {
          to { opacity: 0; transform: translateX(-50%) translateY(-10px); }
        }
      `}</style>
    </div>
  );
};

// ============================================
// 4. CONFIRM MODAL (legacy)
// ============================================
const ConfirmModal = ({ message, onConfirm, onClose }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      backdropFilter: 'blur(4px)',
      zIndex: 10001,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Avenir, sans-serif',
    }} onClick={onClose}>
      <div style={{
        backgroundColor: '#FAFAFA',
        borderRadius: '20px',
        padding: '28px 32px',
        maxWidth: '400px',
        width: '90%',
        textAlign: 'center',
        border: '1px solid #eaeaea',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: '#ffebee',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px auto',
        }}>
          <FiTrash2 size={28} color="#e02f1d" />
        </div>
        <h3 style={{
          fontFamily: 'Avenir, sans-serif',
          fontWeight: 600,
          fontSize: '1.2rem',
          color: '#333',
          margin: '0 0 8px 0',
        }}>
          Delete?
        </h3>
        <p style={{
          fontFamily: 'Avenir, sans-serif',
          fontSize: '0.9rem',
          color: '#666',
          margin: '0 0 24px 0',
          lineHeight: 1.5,
        }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button onClick={onClose} style={{
            padding: '10px 24px',
            borderRadius: '30px',
            border: '1px solid #ddd',
            background: 'white',
            color: '#666',
            cursor: 'pointer',
            fontFamily: 'Avenir, sans-serif',
            fontSize: '0.85rem',
            fontWeight: 500,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.borderColor = '#ccc'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#ddd'; }}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{
            padding: '10px 24px',
            borderRadius: '30px',
            border: 'none',
            background: '#e02f1d',
            color: 'white',
            cursor: 'pointer',
            fontFamily: 'Avenir, sans-serif',
            fontSize: '0.85rem',
            fontWeight: 600,
            transition: 'background 0.2s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#c62828'}
          onMouseLeave={e => e.currentTarget.style.background = '#e02f1d'}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// 5. FULL‑SCREEN MODAL (redesigned with flows)
// ============================================
const FullScreenModal = ({ config, onClose }) => {
  const { trigger, plan, remaining, onUpgrade, onAddBalance, initialView } = config;
  const [view, setView] = useState(initialView === 'add-balance' ? 'add-balance' : 'main'); // 'main' | 'upgrade' | 'add-balance'

  // ----- Add Balance specific state -----
  const [selectedTopUpAmount, setSelectedTopUpAmount] = useState(null); // total payment amount
  const [customAmountText, setCustomAmountText] = useState('');

  // ----- Upgrade specific state -----
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);

  // ----- Loading state for circular progress (sequential reveal) -----
  const [isLoading, setIsLoading] = useState(true);
  const [loaderVisible, setLoaderVisible] = useState(false);

  // Sequential reveal: background fades in first, then loader after 300ms
  useEffect(() => {
    const bgTimer = setTimeout(() => {
      setLoaderVisible(true);
    }, 300);
    const contentTimer = setTimeout(() => {
      setIsLoading(false);
    }, 1800); // background fade (0.4s) + loader delay (0.3s) + circle fill (1s) + small buffer
    return () => {
      clearTimeout(bgTimer);
      clearTimeout(contentTimer);
    };
  }, []);

  // ----- Determine trigger category -----
  const isPlanRelated = [
    FULLSCREEN_TRIGGERS.TRIAL_ENDED,
    FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_ITERATIONS,
    FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_VIDEOS,
  ].includes(trigger);

  const isAccountIssue = [
    FULLSCREEN_TRIGGERS.ACCOUNT_SUSPENDED,
    FULLSCREEN_TRIGGERS.TOS_VIOLATION,
  ].includes(trigger);

  // ----- Titles and descriptions per trigger -----
  const getTitleAndDescription = () => {
    switch (trigger) {
      case FULLSCREEN_TRIGGERS.TRIAL_ENDED:
        return {
          title: 'Your trial has ended',
          description: 'Your 3‑day free trial is over. Upgrade to continue using all Outlne features.',
        };
      case FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_ITERATIONS:
        return {
          title: 'Not enough balance for iterations',
          description: 'You don’t have enough balance to generate iterations. Add balance or upgrade your plan.',
        };
      case FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_VIDEOS:
        return {
          title: 'Not enough balance for videos',
          description: 'You don’t have enough balance to generate videos. Add balance or upgrade your plan.',
        };
      case FULLSCREEN_TRIGGERS.ACCOUNT_SUSPENDED:
        return {
          title: 'Account suspended',
          description: 'Your account has been suspended. Please contact support for further assistance.',
        };
      case FULLSCREEN_TRIGGERS.TOS_VIOLATION:
        return {
          title: 'Terms of Service violation',
          description: 'Your account has been flagged for a Terms of Service violation. Contact support if you believe this is a mistake.',
        };
      default:
        return {
          title: 'Notification',
          description: '',
        };
    }
  };

  const { title, description } = getTitleAndDescription();

  // Show "Add Balance" only for paid plans and not during trial
  const isTrialEnded = trigger === FULLSCREEN_TRIGGERS.TRIAL_ENDED;
  const showAddBalance = !isTrialEnded && (plan === 'creator' || plan === 'pro') && isPlanRelated;

  const formatNumber = (num) => num?.toLocaleString() || 0;

  // Current plan details (only relevant for plan-related triggers)
  const planCaps = PLAN_CAPS[plan] || PLAN_CAPS.starter;
  const planPrice = PLAN_PRICES[plan] || 0;

  // Top‑up helpers – 10% service fee added to generation budget
  const getGenBudgetFromTotal = (total) => {
    return Math.round((total / (1 + SERVICE_FEE_RATE)) * 100) / 100;
  };

  const getServiceFee = (total) => {
    const gen = getGenBudgetFromTotal(total);
    return Math.round((total - gen) * 100) / 100;
  };

  const getTopUpCapacity = (total) => {
    const genBudget = getGenBudgetFromTotal(total);
    const iter = Math.floor(genBudget / ITERATION_DOLLAR_COST);
    const vid = Math.floor(genBudget / VIDEO_DOLLAR_COST);
    return { maxIterations: iter, maxVideos: vid, genBudget, serviceFee: getServiceFee(total) };
  };

  // Fixed top‑up amounts (total payment)
  const getTopUpFixedAmounts = () => [10, 20, 35, 50];

  // Maximum custom amount (total payment)
  const maxCustomAmount = plan === 'pro' ? 1000 : 100;

  const handleSelectFixedAmount = (amount) => {
    setSelectedTopUpAmount(amount);
    setCustomAmountText('');
  };

  const handleCustomAmountChange = (e) => {
    const val = e.target.value;
    setCustomAmountText(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      const clamped = Math.min(num, maxCustomAmount);
      setSelectedTopUpAmount(clamped);
      if (clamped !== num) setCustomAmountText(String(clamped));
    } else {
      setSelectedTopUpAmount(null);
    }
  };

  const handleContinueTopUp = () => {
    if (!selectedTopUpAmount || selectedTopUpAmount <= 0) return;
    const { maxIterations, maxVideos, genBudget, serviceFee } = getTopUpCapacity(selectedTopUpAmount);
    alert(
      `🔜 Redirect to Square payment for $${selectedTopUpAmount}\n` +
      `Service fee: $${serviceFee}\n` +
      `Generation budget added: $${genBudget}\n` +
      `Up to ${maxIterations.toLocaleString()} iterations or ${maxVideos} videos.`
    );
  };

  const handleContinueUpgrade = () => {
    if (!selectedUpgradePlan) return;
    alert(`🔜 Redirect to Square to upgrade to ${selectedUpgradePlan.charAt(0).toUpperCase() + selectedUpgradePlan.slice(1)} plan.`);
    closeModal();
  };

  // Navigation helpers
  const goToUpgrade = () => {
    setView('upgrade');
    setSelectedUpgradePlan(null);
  };

  const goToAddBalance = () => {
    if (onAddBalance) {
      onAddBalance();
    } else {
      setView('add-balance');
      setSelectedTopUpAmount(null);
      setCustomAmountText('');
    }
  };

  const goBack = () => {
    setView('main');
    setSelectedTopUpAmount(null);
    setCustomAmountText('');
    setSelectedUpgradePlan(null);
  };

  const closeModal = () => {
    setSelectedTopUpAmount(null);
    setCustomAmountText('');
    setSelectedUpgradePlan(null);
    onClose();
  };

  // Reusable dark plan summary card
  const DarkPlanSummary = ({ showBack = false }) => (
    <div style={{
      background: '#2D2D2D',
      padding: '20px 24px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: 'inset 2px 0 0 0 #88B7FF, inset -2px 0 0 0 #88B7FF, inset 0 2px 0 0 #88B7FF',
      borderRadius: '24px 24px 0 0',
      marginBottom: '-1px',
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
        pointerEvents: 'none',
        maskImage: 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        zIndex: 0,
      }} />
      {showBack && (
        <button onClick={goBack} style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#fff',
          padding: '4px',
          borderRadius: '50%',
          transition: 'background 0.2s',
          zIndex: 2,
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <FiArrowLeft size={24} />
        </button>
      )}
      <button onClick={closeModal} style={{
        position: 'absolute',
        top: '12px',
        right: '12px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#fff',
        padding: '4px',
        borderRadius: '50%',
        transition: 'background 0.2s',
        zIndex: 2,
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <FiX size={24} />
      </button>
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <div style={{ marginBottom: '12px' }}>
          <div style={{ color: 'white', fontSize: '0.8rem', fontWeight: 500, opacity: 0.7 }}>Your Plan</div>
          <div style={{ color: 'white', fontSize: '1.2rem', fontWeight: 600 }}>
            {plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : 'Starter'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <div style={{
            background: '#595959',
            borderRadius: '20px',
            padding: '6px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'white',
            fontSize: '0.8rem',
            fontFamily: 'Avenir, sans-serif',
            whiteSpace: 'nowrap',
          }}>
            <RiImageCircleAiLine size={16} />
            <span>{formatNumber(remaining?.iterations ?? 0)}</span>
          </div>
          <div style={{
            background: '#595959',
            borderRadius: '20px',
            padding: '6px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'white',
            fontSize: '0.8rem',
            fontFamily: 'Avenir, sans-serif',
            whiteSpace: 'nowrap',
          }}>
            <RiVideoOnAiLine size={16} />
            <span>{formatNumber(remaining?.videos ?? 0)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================
  // LOADING STATE – sequential reveal
  // ============================================
  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(10px)',
          zIndex: 20000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'overlayFadeIn 0.4s ease-out forwards',
          opacity: 0,
        }}
        onClick={closeModal}
      >
        <style>{`
          @keyframes overlayFadeIn {
            to { opacity: 1; }
          }
          @keyframes loaderFadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
        `}</style>
        {loaderVisible && (
          <div style={{ animation: 'loaderFadeIn 0.3s ease-out forwards' }}>
            <CircularLoader size={60} strokeWidth={5} trackColor="rgba(255,255,255,0.2)" fillColor="#88B7FF" duration={1} />
          </div>
        )}
      </div>
    );
  }

  // ============================================
  // SIMPLE ALERT MODAL (account issues)
  // ============================================
  if (view === 'main' && isAccountIssue) {
    let iconComponent;
    switch (trigger) {
      case FULLSCREEN_TRIGGERS.ACCOUNT_SUSPENDED:
        iconComponent = <FiAlertTriangle size={48} color="#ff9800" />;
        break;
      case FULLSCREEN_TRIGGERS.TOS_VIOLATION:
        iconComponent = <FiAlertCircle size={48} color="#f44336" />;
        break;
      default:
        iconComponent = <FiAlertCircle size={48} color="#88B7FF" />;
    }
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(10px)',
        zIndex: 20000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Avenir, sans-serif',
        animation: 'fadeIn 0.2s ease-out',
      }} onClick={closeModal}>
        <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        <div style={{
          background: '#FAFAFA',
          borderRadius: '24px',
          maxWidth: '480px',
          width: '100%',
          padding: '32px 24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          textAlign: 'center',
          position: 'relative',
        }} onClick={e => e.stopPropagation()}>
          <button onClick={closeModal} style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#888',
            padding: '4px',
            borderRadius: '50%',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <FiX size={24} />
          </button>
          <div style={{ marginBottom: '16px' }}>
            {iconComponent}
          </div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#333', marginBottom: '8px' }}>
            {title}
          </h2>
          <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: 1.6, marginBottom: '28px' }}>
            {description}
          </p>
          <button onClick={closeModal} style={{
            padding: '14px 48px',
            borderRadius: '30px',
            border: 'none',
            background: '#88B7FF',
            color: 'white',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.2s',
            fontFamily: 'Avenir, sans-serif',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#6a9fdf'}
          onMouseLeave={e => e.currentTarget.style.background = '#88B7FF'}>
            OK
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN VIEW (plan‑related: trial ended, not enough balance)
  // ============================================
  if (view === 'main') {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(10px)',
        zIndex: 20000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Avenir, sans-serif',
        animation: 'fadeIn 0.2s ease-out',
      }} onClick={closeModal}>
        <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        <div style={{
          background: '#FAFAFA',
          borderRadius: '24px',
          maxWidth: '480px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          position: 'relative',
          overflow: 'hidden',
        }} onClick={e => e.stopPropagation()}>
          <DarkPlanSummary />
          <div style={{ padding: '24px 24px 32px 24px', textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              {isTrialEnded ? (
                <FiAlertTriangle size={48} color="#ff9800" />
              ) : (
                <FiAlertCircle size={48} color="#88B7FF" />
              )}
            </div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#333', marginBottom: '8px' }}>
              {title}
            </h2>
            <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: 1.6, marginBottom: '28px' }}>
              {description}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button onClick={goToUpgrade} style={{
                padding: '14px',
                borderRadius: '30px',
                border: 'none',
                background: '#88B7FF',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s',
                fontFamily: 'Avenir, sans-serif',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#6a9fdf'}
              onMouseLeave={e => e.currentTarget.style.background = '#88B7FF'}>
                Upgrade Account
              </button>
              {showAddBalance && (
                <button onClick={goToAddBalance} style={{
                  padding: '14px',
                  borderRadius: '30px',
                  border: '1px solid #88B7FF',
                  background: 'transparent',
                  color: '#88B7FF',
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontFamily: 'Avenir, sans-serif',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = '#88B7FF';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#88B7FF';
                }}>
                  Add Balance
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== UPGRADE VIEW =====
  if (view === 'upgrade') {
    let upgradeOptions = [];
    if (plan === 'starter') {
      upgradeOptions = ['creator', 'pro'];
    } else if (plan === 'creator') {
      upgradeOptions = ['pro'];
    } else if (plan === 'pro') {
      upgradeOptions = ['creator'];
    } else {
      upgradeOptions = ['creator', 'pro'];
    }

    const allPlans = [plan, ...upgradeOptions.filter(p => p !== plan)];

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(10px)',
        zIndex: 20000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Avenir, sans-serif',
        animation: 'fadeIn 0.2s ease-out',
      }} onClick={closeModal}>
        <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        <div style={{
          background: '#FAFAFA',
          borderRadius: '24px',
          maxWidth: '480px',
          width: '100%',
          padding: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          position: 'relative',
        }} onClick={e => e.stopPropagation()}>
          <button onClick={goBack} style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#888',
            padding: '4px',
            borderRadius: '50%',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <FiArrowLeft size={24} />
          </button>
          <h3 style={{ textAlign: 'center', fontSize: '1.3rem', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
            Upgrade Your Plan
          </h3>
          <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#888', marginBottom: '20px' }}>
            Choose a plan that fits your needs
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
            {allPlans.map(p => {
              const caps = PLAN_CAPS[p];
              const isCurrent = p === plan;
              const isSelected = selectedUpgradePlan === p;

              let border;
              if (isCurrent && selectedUpgradePlan && selectedUpgradePlan !== p) {
                border = '1px solid #ddd';
              } else if (isCurrent && !selectedUpgradePlan) {
                border = '2px solid #88B7FF';
              } else {
                border = isSelected ? '2px solid #88B7FF' : '1px solid #ddd';
              }

              const bg = isCurrent
                ? (selectedUpgradePlan && selectedUpgradePlan !== p ? 'white' : '#f0f7ff')
                : (isSelected ? '#f0f7ff' : 'white');

              if (p === 'starter' && !isCurrent) return null;

              return (
                <div key={p}
                  onClick={() => !isCurrent && setSelectedUpgradePlan(p)}
                  style={{
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border,
                    background: bg,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: isCurrent ? 'default' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: isCurrent ? 0.8 : 1,
                  }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#333', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                      {isCurrent && (
                        <span style={{ fontSize: '0.7rem', color: '#88B7FF', marginLeft: '4px' }}>(Current)</span>
                      )}
                      {!isCurrent && (
                        <span style={{ fontSize: '0.85rem', color: '#555', fontWeight: 400 }}>
                          {PLAN_PRICES[p] === 0 ? 'Free' : `$${PLAN_PRICES[p]}/mo`}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#888' }}>
                      {caps.iterationCap} iterations · {caps.videoCap} videos
                    </div>
                  </div>
                  <div>
                    {isCurrent ? (
                      <FiCheck size={20} color="#88B7FF" />
                    ) : (
                      isSelected ? (
                        <span style={{ fontSize: '0.8rem', color: '#2a6cb6', fontWeight: 500 }}>Selected</span>
                      ) : (
                        <span style={{ fontSize: '0.8rem', color: '#88B7FF', fontWeight: 500 }}>Select</span>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              onClick={handleContinueUpgrade}
              disabled={!selectedUpgradePlan}
              style={{
                padding: '14px',
                borderRadius: '30px',
                border: 'none',
                background: selectedUpgradePlan ? '#88B7FF' : '#ccc',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: selectedUpgradePlan ? 'pointer' : 'default',
                transition: 'background 0.2s',
                fontFamily: 'Avenir, sans-serif',
                opacity: selectedUpgradePlan ? 1 : 0.6,
              }}
              onMouseEnter={e => { if (selectedUpgradePlan) e.currentTarget.style.background = '#6a9fdf'; }}
              onMouseLeave={e => { if (selectedUpgradePlan) e.currentTarget.style.background = '#88B7FF'; }}>
              Continue to Payment
            </button>
            <button onClick={goBack} style={{
              padding: '14px',
              borderRadius: '30px',
              border: '1px solid #ddd',
              background: 'transparent',
              color: '#666',
              fontSize: '1rem',
              fontWeight: 500,
              cursor: 'pointer',
              width: '100%',
              transition: 'all 0.2s',
              fontFamily: 'Avenir, sans-serif',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.borderColor = '#ccc'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#ddd'; }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== ADD BALANCE VIEW (transparent total + service fee) =====
  if (view === 'add-balance') {
    const fixedAmounts = getTopUpFixedAmounts();
    const isAmountSelected = selectedTopUpAmount !== null && selectedTopUpAmount > 0;
    const capacity = isAmountSelected ? getTopUpCapacity(selectedTopUpAmount) : null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(10px)',
        zIndex: 20000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Avenir, sans-serif',
        animation: 'fadeIn 0.2s ease-out',
      }} onClick={closeModal}>
        <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        <div style={{
          background: '#FAFAFA',
          borderRadius: '24px',
          maxWidth: '480px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          position: 'relative',
          overflow: 'hidden',
        }} onClick={e => e.stopPropagation()}>

          <DarkPlanSummary showBack={true} />

          <div style={{ padding: '24px 24px 32px 24px' }}>
            <h3 style={{ textAlign: 'center', fontSize: '1.2rem', fontWeight: 600, color: '#333', marginBottom: '6px' }}>
              Add Balance
            </h3>
            <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#666', marginBottom: '24px' }}>
              Select a payment amount – a 10% service fee is included. You’ll receive the generation budget shown below.
            </p>

            {/* Fixed amount buttons (total payment) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              {fixedAmounts.map(amount => {
                const isSelected = selectedTopUpAmount === amount;
                return (
                  <button key={amount}
                    onClick={() => handleSelectFixedAmount(amount)}
                    style={{
                      padding: '14px 10px',
                      borderRadius: '12px',
                      border: isSelected ? '2px solid #88B7FF' : '1px solid #ddd',
                      background: isSelected ? '#f0f7ff' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontWeight: 600,
                      fontSize: '1.2rem',
                      color: isSelected ? '#2a6cb6' : '#333',
                      fontFamily: 'Avenir, sans-serif',
                    }}
                    onMouseEnter={e => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#88B7FF';
                        e.currentTarget.style.background = '#f5f9ff';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#ddd';
                        e.currentTarget.style.background = 'white';
                      }
                    }}>
                    ${amount}
                  </button>
                );
              })}
            </div>

            {/* Custom amount input (total payment) */}
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#888',
                  fontSize: '1rem'
                }}>$</span>
                <input
                  type="number"
                  min="1"
                  max={maxCustomAmount}
                  step="1"
                  placeholder={`Custom amount (up to $${maxCustomAmount})`}
                  value={customAmountText}
                  onChange={handleCustomAmountChange}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 28px',
                    borderRadius: '12px',
                    border: selectedTopUpAmount && !fixedAmounts.includes(selectedTopUpAmount)
                      ? '2px solid #88B7FF'
                      : '1px solid #ddd',
                    background: selectedTopUpAmount && !fixedAmounts.includes(selectedTopUpAmount)
                      ? '#f0f7ff'
                      : 'white',
                    fontSize: '1rem',
                    fontFamily: 'Avenir, sans-serif',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border 0.2s',
                  }}
                />
              </div>
            </div>

            {/* Selected amount summary – transparent breakdown */}
            {isAmountSelected && (
              <div style={{
                background: '#f4f9ff',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '20px',
                border: '1px solid #d4e5ff',
                fontSize: '0.85rem',
                color: '#333',
                textAlign: 'center',
                lineHeight: 1.5,
              }}>
                Total: <strong>${selectedTopUpAmount}</strong><br />
                Service fee (10%): <strong>${capacity.serviceFee}</strong><br />
                Generation budget: <strong>${capacity.genBudget}</strong><br />
                Up to <strong>{capacity.maxIterations.toLocaleString()}</strong> iterations or{' '}
                <strong>{capacity.maxVideos}</strong> videos
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={handleContinueTopUp}
                disabled={!isAmountSelected}
                style={{
                  padding: '14px',
                  borderRadius: '30px',
                  border: 'none',
                  background: isAmountSelected ? '#88B7FF' : '#ccc',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: isAmountSelected ? 'pointer' : 'default',
                  transition: 'background 0.2s',
                  fontFamily: 'Avenir, sans-serif',
                  opacity: isAmountSelected ? 1 : 0.6,
                }}
                onMouseEnter={e => { if (isAmountSelected) e.currentTarget.style.background = '#6a9fdf'; }}
                onMouseLeave={e => { if (isAmountSelected) e.currentTarget.style.background = '#88B7FF'; }}>
                Continue to Payment
              </button>
              <button
                onClick={goBack}
                style={{
                  padding: '14px',
                  borderRadius: '30px',
                  border: '1px solid #ddd',
                  background: 'transparent',
                  color: '#666',
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.2s',
                  fontFamily: 'Avenir, sans-serif',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.borderColor = '#ccc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#ddd'; }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// ============================================
// 6. CIRCULAR LOADER COMPONENT
// ============================================
const CircularLoader = ({ size = 60, strokeWidth = 5, trackColor = '#e0e0e0', fillColor = '#88B7FF', duration = 1 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={fillColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference}
          style={{
            animation: `circularFill ${duration}s ease-in-out forwards`,
          }}
        />
      </svg>
      <style>{`
        @keyframes circularFill {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
};

// ============================================
// 7. TUTORIAL MODAL (moved from Editor)
// ============================================
const TutorialModal = ({ onClose, voiceEnabled, onToggleVoice }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const slides = [
    {
      title: 'Welcome to Outlne',
      description: 'Just a few things before you start renovation planning for your home. With ease.',
      image: 'https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2Ftutorial%2Fslide1.png?alt=media',
    },
    {
      title: 'How It Works',
      description: 'Use the Pen Lasso to select any area of the photo, then speak or type a prompt to generate new content. Refine with variations.',
      image: 'https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2Ftutorial%2Fslide2.png?alt=media',
    },
    {
      title: 'Speak & Create',
      description: 'Enable voice commands to generate without typing. Toggle on/off anytime.',
      image: 'https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2Ftutorial%2Fslide3.png?alt=media',
      hasToggle: true,
    },
    {
      title: 'Start Designing',
      description: 'You’re all done! Be sure to watch official tutorials for tips and techniques, and explore the platform to find your own creative workflow.',
      image: 'https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/assets%2Ftutorial%2Fslide4.png?alt=media',
    },
  ];

  const totalSlides = slides.length;
  const slide = slides[currentSlide];

  const handleNext = () => {
    if (currentSlide === totalSlides - 1) {
      if (dontShowAgain) {
        localStorage.setItem('editorTutorialShown', 'true');
      }
      onClose();
    } else {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) setCurrentSlide(currentSlide - 1);
  };

  const handleSkip = () => {
    if (dontShowAgain) {
      localStorage.setItem('editorTutorialShown', 'true');
    }
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backdropFilter: 'blur(8px)',
      backgroundColor: 'transparent',
      zIndex: 20000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: 'Avenir, sans-serif',
    }} onClick={handleSkip}>
      <div style={{
        background: '#FAFAFA',
        borderRadius: '24px',
        maxWidth: '520px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        position: 'relative',
        overflow: 'hidden',
        padding: '24px 24px 20px 24px',
      }} onClick={e => e.stopPropagation()}>
        <button onClick={handleSkip} style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#888',
          padding: '4px',
          borderRadius: '50%',
          transition: 'background 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <FiX size={24} />
        </button>

        <div style={{
          width: '100%',
          aspectRatio: '16/9',
          borderRadius: '12px',
          overflow: 'hidden',
          marginBottom: '16px',
          background: '#f0f0f0',
        }}>
          <img src={slide.image} alt={slide.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>

        <h2 style={{
          fontSize: '1.4rem',
          fontWeight: 600,
          color: '#333',
          marginBottom: '8px',
          fontFamily: 'Avenir, sans-serif',
        }}>
          {slide.title}
        </h2>
        <p style={{
          fontSize: '0.95rem',
          color: '#555',
          lineHeight: 1.6,
          marginBottom: '16px',
          fontFamily: 'Avenir, sans-serif',
        }}>
          {slide.description}
        </p>

        {slide.hasToggle && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: '#f5f5f5',
            borderRadius: '12px',
            marginBottom: '20px',
          }}>
            <span style={{ fontWeight: 500, color: '#333', fontFamily: 'Avenir, sans-serif' }}>Speak & Create Mode</span>
            <label className="settings-switch" style={{ margin: 0 }}>
              <input
                type="checkbox"
                checked={voiceEnabled}
                onChange={onToggleVoice}
              />
              <span className="settings-slider"></span>
            </label>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {currentSlide > 0 && (
                <button onClick={handlePrev} style={{
                  padding: '8px 20px',
                  borderRadius: '30px',
                  border: '1px solid #ddd',
                  background: 'transparent',
                  color: '#666',
                  cursor: 'pointer',
                  fontFamily: 'Avenir, sans-serif',
                  fontSize: '0.85rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.borderColor = '#ccc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#ddd'; }}>
                  ← Back
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {slides.map((_, idx) => (
                <div key={idx} style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: idx === currentSlide ? '#88B7FF' : '#ddd',
                  transition: 'background 0.2s',
                }} />
              ))}
            </div>
            <button onClick={handleNext} style={{
              padding: '8px 24px',
              borderRadius: '30px',
              border: 'none',
              background: 'linear-gradient(135deg, #88b7ff, #6a9cff)',
              color: 'white',
              cursor: 'pointer',
              fontFamily: 'Avenir, sans-serif',
              fontSize: '0.85rem',
              fontWeight: 600,
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 4px 12px rgba(136, 183, 255, 0.3)',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(136, 183, 255, 0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(136, 183, 255, 0.3)'; }}>
              {currentSlide === totalSlides - 1 ? 'Get Started' : 'Next →'}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="dontShowAgain"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <label htmlFor="dontShowAgain" style={{
              fontSize: '0.8rem',
              color: '#888',
              fontFamily: 'Avenir, sans-serif',
              cursor: 'pointer',
            }}>
              Do not show this again
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// 8. COMING SOON MODAL (hero‑style banner)
// ============================================
const ComingSoonModal = ({ title, description, image, launchDate, onClose }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backdropFilter: 'blur(8px)',
      backgroundColor: 'rgba(0,0,0,0.3)',
      zIndex: 20000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: 'Avenir, sans-serif',
    }} onClick={onClose}>
      <div style={{
        background: '#FAFAFA',
        borderRadius: '24px',
        maxWidth: '560px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        position: 'relative',
        overflow: 'hidden',
        padding: '32px 32px 28px',
        textAlign: 'center',
      }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#888',
          padding: '4px',
          borderRadius: '50%',
          transition: 'background 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <FiX size={24} />
        </button>

        {image && (
          <div style={{
            width: '100%',
            maxHeight: '200px',
            borderRadius: '16px',
            overflow: 'hidden',
            marginBottom: '24px',
          }}>
            <img src={image} alt={title} style={{ width: '100%', height: 'auto', objectFit: 'cover' }} />
          </div>
        )}

        <h2 style={{
          fontSize: '1.8rem',
          fontWeight: 700,
          color: '#333',
          marginBottom: '12px',
          letterSpacing: '-0.5px',
        }}>
          {title || 'Coming Soon'}
        </h2>

        <p style={{
          fontSize: '1rem',
          color: '#555',
          lineHeight: 1.6,
          marginBottom: '16px',
          maxWidth: '440px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          {description || 'This feature is under development. Stay tuned for updates!'}
        </p>

        {launchDate && (
          <p style={{
            textAlign: 'center',
            fontSize: '0.9rem',
            color: '#88B7FF',
            fontWeight: 500,
            marginBottom: '24px',
          }}>
            {launchDate}
          </p>
        )}

        <button onClick={onClose} style={{
          padding: '12px 40px',
          borderRadius: '30px',
          border: 'none',
          background: 'linear-gradient(135deg, #88b7ff, #6a9cff)',
          color: 'white',
          cursor: 'pointer',
          fontFamily: 'Avenir, sans-serif',
          fontSize: '1rem',
          fontWeight: 600,
          transition: 'transform 0.2s, box-shadow 0.2s',
          boxShadow: '0 4px 16px rgba(136, 183, 255, 0.3)',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(136, 183, 255, 0.4)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(136, 183, 255, 0.3)'; }}>
          Got it
        </button>
      </div>
    </div>
  );
};

// ============================================
// 9. COMING SOON LIST MODAL (hero with feature list)
// ============================================
const ComingSoonListModal = ({ title, description, items, image, launchDate, onClose }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backdropFilter: 'blur(8px)',
      backgroundColor: 'rgba(0,0,0,0.3)',
      zIndex: 20000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: 'Avenir, sans-serif',
    }} onClick={onClose}>
      <div style={{
        background: '#FAFAFA',
        borderRadius: '24px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        position: 'relative',
        overflow: 'hidden',
        padding: '32px 32px 28px',
        display: 'flex',
        flexDirection: 'column',
      }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#888',
          padding: '4px',
          borderRadius: '50%',
          transition: 'background 0.2s',
          zIndex: 2,
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <FiX size={24} />
        </button>

        {image && (
          <div style={{
            width: '100%',
            maxHeight: '160px',
            borderRadius: '16px',
            overflow: 'hidden',
            marginBottom: '20px',
          }}>
            <img src={image} alt={title} style={{ width: '100%', height: 'auto', objectFit: 'cover' }} />
          </div>
        )}

        <h2 style={{
          fontSize: '1.8rem',
          fontWeight: 700,
          color: '#333',
          marginBottom: '8px',
          letterSpacing: '-0.5px',
        }}>
          {title || 'Coming Soon'}
        </h2>

        <p style={{
          fontSize: '1rem',
          color: '#555',
          lineHeight: 1.6,
          marginBottom: '24px',
        }}>
          {description || 'We are working on exciting new features. Here’s what’s coming:'}
        </p>

        <div style={{
          flex: 1,
          overflowY: 'auto',
          paddingRight: '4px',
          marginBottom: '24px',
        }}>
          {items && items.map((item, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
              padding: '14px 0',
              borderBottom: index < items.length - 1 ? '1px solid #f0f0f0' : 'none',
            }}>
              <div style={{
                minWidth: '36px',
                textAlign: 'center',
                color: '#88B7FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {item.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '6px',
                }}>
                  <span style={{
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#333',
                  }}>{item.label}</span>
                </div>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  marginTop: '2px',
                  lineHeight: 1.4,
                }}>{item.description}</div>
              </div>
            </div>
          ))}
        </div>

        {launchDate && (
          <p style={{
            textAlign: 'center',
            fontSize: '0.9rem',
            color: '#666',
            marginBottom: '16px',
          }}>
            {launchDate}
          </p>
        )}

        <button onClick={onClose} style={{
          padding: '12px 40px',
          borderRadius: '30px',
          border: 'none',
          background: 'linear-gradient(135deg, #88b7ff, #6a9cff)',
          color: 'white',
          cursor: 'pointer',
          fontFamily: 'Avenir, sans-serif',
          fontSize: '1rem',
          fontWeight: 600,
          transition: 'transform 0.2s, box-shadow 0.2s',
          boxShadow: '0 4px 16px rgba(136, 183, 255, 0.3)',
          alignSelf: 'center',
          minWidth: '140px',
                }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(136, 183, 255, 0.4)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(136, 183, 255, 0.3)'; }}>
          Got it
        </button>
      </div>
    </div>
  );
};

// ============================================
// SECTION OVERLAYS CONFIG
// ============================================
export const SECTION_OVERLAYS = {
  'interior-design': {
    title: 'Coming Soon',
    description: "We're building a curated collection of interior design inspiration. Stay tuned!",
    icon: <LuConstruction size={40} color="#88B7FF" />,
  },
  // Add more sections here as needed
};

// ============================================
// 10. COMING SOON OVERLAY (for sections)
// ============================================
export const ComingSoonOverlay = ({ section, title, description, icon, style = {} }) => {
  // If a section key is provided, look up the config
  const config = section ? SECTION_OVERLAYS[section] : null;
  const finalTitle = title || config?.title || 'Coming Soon';
  const finalDescription = description || config?.description || 'We are working on something great. Stay tuned!';
  const finalIcon = icon || config?.icon || <LuConstruction size={40} color="#88B7FF" />;

  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(4px)',
      borderRadius: 'inherit',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      padding: '20px',
      textAlign: 'center',
      ...style,
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '12px', lineHeight: 1 }}>{finalIcon}</div>
      <h3 style={{
        fontFamily: 'Avenir, sans-serif',
        fontWeight: 600,
        fontSize: '1.2rem',
        color: '#333',
        marginBottom: '8px',
      }}>{finalTitle}</h3>
      <p style={{
        fontFamily: 'Avenir, sans-serif',
        fontSize: '0.9rem',
        color: '#666',
        maxWidth: '300px',
        margin: '0 auto',
      }}>{finalDescription}</p>
    </div>
  );
};